import React, { useState, useEffect } from 'react';
import { checkValidEmail, sendNotification, getProfessionalSituation } from '../helper/Api/AdminApi';
import { encryptAESCryptoJS } from '../helper/crypto';
import { toast } from 'react-toastify';
import { FormQuestion, MultiSelectQuestion } from './child/QuestionLayer';

const SendNotificationModal = ({
    showModal,
    onClose,
    onSuccess,
    loading: parentLoading
}) => {
    const [showOtherOptions, setShowOtherOptions] = useState(false);
    const [showFilterQuestions, setShowFilterQuestions] = useState(false);
    const [showSingleOption, setShowSingleOption] = useState(false);
    const [loading, setLoading] = useState(false);
    const [professionalSituations, setProfessionalSituations] = useState([]);
    const [loadingSituations, setLoadingSituations] = useState(false);

    const [formData, setFormData] = useState({
        Title: '',
        Description: '',
        IsSingleUser: false,
        IsAllUser: false,
        UserID: '',
        EmailID: '',
        SubscriptionDetailForFilteration: null
    });

    const [filterCriteria, setFilterCriteria] = useState({
        HorizonDePlacement: '',
        ObjectifDePlacement: [],
        AversionAuRisque: '',
        SituationProfessionnelle: ''
    });

    // Same formQuestions object
    const formQuestions = {
        horizon: {
            question: "Quel est votre horizon de placement ?",
            options: [
                "Inférieur à 1 an",
                "Entre 1 an et 5 ans",
                "Supérieur à 5 ans"
            ],
            type: "single"
        },
        objectif: {
            question: "Quel est votre objectif de placement ?",
            options: [
                "Construire un capital",
                "Générer un revenu complémentaire",
                "Préparer ma retraite",
                "Spéculer à court terme"
            ],
            type: "multi"
        },
        aversion: {
            question: "Quel est votre aversion au risque ?",
            options: [
                "Conservateur - Je souhaite minimiser le risque de perdre une partie de mon capital, quitte à générer uniquement un rendement minimal",
                "Modéré - J'accepte de prendre un risque modéré pour générer un rendement intermédiaire",
                "Entrepreneur - J'accepte une grande volatilité sur mon capital en vue de générer un rendement maximal"
            ],
            type: "single"
        },
        situation: {
            question: "Situation professionnelle",
            type: "dropdown",
            field: "SituationProfessionnelle"
        }
    };

    useEffect(() => {
        if (showModal) {
            fetchProfessionalSituations();
        }
    }, [showModal]);

    useEffect(() => {
        if (showModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [showModal]);


    const fetchProfessionalSituations = async () => {
        try {
            setLoadingSituations(true);
            const response = await getProfessionalSituation();
            if (response.Status === 1) {
                setProfessionalSituations(response.Data || []);
            }
        } catch (error) {
            console.error('Error fetching professional situations:', error);
            toast.error('Failed to load professional situations');
        } finally {
            setLoadingSituations(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleRadioChange = (type, value) => {
        if (type === 'single') {
            setShowSingleOption(true);
            setShowOtherOptions(false);
            setShowFilterQuestions(false);
            setFormData({
                ...formData,
                IsSingleUser: true,
                IsAllUser: false,
                SubscriptionDetailForFilteration: null
            });
        } else if (type === 'multi') {
            setShowSingleOption(false);
            setShowOtherOptions(true);
            setFormData({
                ...formData,
                IsSingleUser: false,
                IsAllUser: false
            });
        } else if (type === 'all') {
            setShowFilterQuestions(false);
            setFormData({
                ...formData,
                IsSingleUser: false,
                IsAllUser: true,
                SubscriptionDetailForFilteration: null
            });
        } else if (type === 'filter') {
            setShowFilterQuestions(true);
            setFormData({
                ...formData,
                IsSingleUser: false,
                IsAllUser: false,
                SubscriptionDetailForFilteration: filterCriteria
            });
        }
    };

    const handleFilterChange = (field, value) => {
        let cleanedValue = value;

        if (field === 'ObjectifDePlacement') {
            cleanedValue = Array.isArray(value) ? value : [];
        } else if (field === 'AversionAuRisque' && value) {
            if (['Conservateur', 'Modéré', 'Entrepreneur'].includes(value)) {
                cleanedValue = value;
            } else {
                cleanedValue = value.split(' ')[0];
            }
        }

        const newFilterCriteria = { ...filterCriteria, [field]: cleanedValue };
        setFilterCriteria(newFilterCriteria);

        const subscriptionDetail = {};
        if (newFilterCriteria.HorizonDePlacement) {
            subscriptionDetail.HorizonDePlacement = newFilterCriteria.HorizonDePlacement;
        }
        if (Array.isArray(newFilterCriteria.ObjectifDePlacement) && newFilterCriteria.ObjectifDePlacement.length > 0) {
            subscriptionDetail.ObjectifDePlacement = newFilterCriteria.ObjectifDePlacement;
        }
        if (newFilterCriteria.AversionAuRisque) {
            subscriptionDetail.AversionAuRisque = newFilterCriteria.AversionAuRisque;
        }
        if (newFilterCriteria.SituationProfessionnelle) {
            subscriptionDetail.SituationProfessionnelle = newFilterCriteria.SituationProfessionnelle;
        }

        setFormData(prev => ({
            ...prev,
            SubscriptionDetailForFilteration: Object.keys(subscriptionDetail).length > 0 ? subscriptionDetail : null
        }));
    };

    const handleSubmit = async () => {
        try {
            if (!formData.Title.trim()) {
                toast.error('Please enter a title');
                return;
            }
            if (!formData.Description.trim()) {
                toast.error('Please enter a description');
                return;
            }

            let userIdToSend = formData.UserID;
            const senderEmail = localStorage.getItem('emailID');

            if (!senderEmail && !formData.IsSingleUser) {
                toast.error('Unable to get sender email. Please login again.');
                return;
            }

            if (formData.IsSingleUser) {
                if (!formData.EmailID.trim()) {
                    toast.error('Please enter email for single user notification');
                    return;
                }

                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.EmailID)) {
                    toast.error('Please enter a valid email address');
                    return;
                }

                setLoading(true);
                const emailResponse = await checkValidEmail(encryptAESCryptoJS(formData.EmailID));

                if (emailResponse.Status !== "1") {
                    toast.error(emailResponse.Message || 'Email not found in the system');
                    setLoading(false);
                    return;
                }

                userIdToSend = emailResponse.UserID;
                setFormData(prev => ({
                    ...prev,
                    UserID: emailResponse.UserID
                }));
            }

            const emailToSend = formData.IsSingleUser
                ? formData.EmailID
                : senderEmail;

            const payload = {
                Title: formData.Title,
                Description: formData.Description,
                IsSingleUser: formData.IsSingleUser,
                IsAllUser: formData.IsAllUser,
                UserID: formData.IsSingleUser ? userIdToSend : null,
                EmailID: emailToSend,
                SubscriptionDetailForFilteration: formData.SubscriptionDetailForFilteration &&
                    Object.keys(formData.SubscriptionDetailForFilteration).length > 0 ?
                    formData.SubscriptionDetailForFilteration : null
            };

            console.log('Payload being sent:', payload);
            const response = await sendNotification(payload);

            if (response.Status === 1 || response.Status === "1") {
                toast.success('Notification sent successfully');
                resetForm();
                onSuccess();
                onClose();
            } else {
                toast.error(response.Message || 'Failed to send notification');
            }
        } catch (error) {
            console.error('Error sending notification:', error);
            toast.error('Failed to send notification');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            Title: '',
            Description: '',
            IsSingleUser: false,
            IsAllUser: false,
            UserID: '',
            EmailID: '',
            SubscriptionDetailForFilteration: null
        });
        setFilterCriteria({
            HorizonDePlacement: '',
            ObjectifDePlacement: [],
            AversionAuRisque: '',
            SituationProfessionnelle: ''
        });
        setShowSingleOption(false);
        setShowOtherOptions(false);
        setShowFilterQuestions(false);
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!showModal) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content border shadow-lg p-16">
                <div className="modal-header p-15 border-bottom">
                    <h4 className="modal-title text-xl fw-semibold text-primary-light">
                        Send New Notification
                    </h4>
                    <button
                        type="button"
                        className="btn-close"
                        onClick={handleClose}
                        disabled={loading || parentLoading}
                    ></button>
                </div>
                <div className="modal-body p-24">
                    <div className="row mb-4">
                        <div className="col-md-12 mb-4">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                                Notification Type <span className="text-danger-600">*</span>
                            </label>
                            <div className="d-flex align-items-center gap-4 pt-10 flex-wrap">
                                <div className="form-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="notificationType"
                                        id="SingleUser"
                                        onChange={() => handleRadioChange('single')}
                                        checked={showSingleOption}
                                        disabled={loading}
                                    />
                                    <label
                                        className="form-check-label text-primary-light text-sm ms-2 mb-0"
                                        htmlFor="SingleUser"
                                    >
                                        Single User
                                    </label>
                                </div>
                                <div className="form-check d-flex align-items-center">
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="notificationType"
                                        id="MultiUser"
                                        onChange={() => handleRadioChange('multi')}
                                        checked={showOtherOptions}
                                        disabled={loading}
                                    />
                                    <label
                                        className="form-check-label text-primary-light text-sm ms-2 mb-0"
                                        htmlFor="MultiUser"
                                    >
                                        Multi User
                                    </label>
                                </div>
                            </div>

                            {showOtherOptions && (
                                <div className="mt-3 d-flex align-items-center gap-4 flex-wrap">
                                    <div className="form-check d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="multiUserType"
                                            id="AllUsers"
                                            onChange={() => handleRadioChange('all')}
                                            checked={formData.IsAllUser && !showFilterQuestions}
                                            disabled={loading}
                                        />
                                        <label
                                            className="form-check-label text-primary-light text-sm ms-2 mb-0"
                                            htmlFor="AllUsers"
                                        >
                                            All Users
                                        </label>
                                    </div>
                                    <div className="form-check d-flex align-items-center">
                                        <input
                                            className="form-check-input"
                                            type="radio"
                                            name="multiUserType"
                                            id="FilterUser"
                                            onChange={() => handleRadioChange('filter')}
                                            checked={showFilterQuestions}
                                            disabled={loading}
                                        />
                                        <label
                                            className="form-check-label text-primary-light text-sm ms-2 mb-0"
                                            htmlFor="FilterUser"
                                        >
                                            Filter User
                                        </label>
                                    </div>
                                </div>
                            )}

                            {showSingleOption && (
                                <div className="my-20">
                                    <label
                                        htmlFor="email"
                                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                                    >
                                        Email <span className="text-danger-600">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        className="form-control radius-8"
                                        id="email"
                                        name="EmailID"
                                        value={formData.EmailID}
                                        onChange={handleInputChange}
                                        placeholder="Enter email address"
                                        disabled={loading}
                                    />
                                </div>
                            )}

                            {showFilterQuestions && (
                                <div className="subscription-box mt-20" style={{ padding: '20px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                                    <FormQuestion
                                        question={formQuestions.horizon.question}
                                        options={formQuestions.horizon.options}
                                        selectedValue={filterCriteria.HorizonDePlacement}
                                        onSelect={(value) => handleFilterChange('HorizonDePlacement', value)}
                                    />

                                    <MultiSelectQuestion
                                        question={formQuestions.objectif.question}
                                        options={formQuestions.objectif.options}
                                        selectedValues={filterCriteria.ObjectifDePlacement}
                                        onSelect={(values) => handleFilterChange('ObjectifDePlacement', values)}
                                    />

                                    <FormQuestion
                                        question={formQuestions.aversion.question}
                                        options={formQuestions.aversion.options}
                                        selectedValue={filterCriteria.AversionAuRisque}
                                        onSelect={(value) => handleFilterChange('AversionAuRisque', value)}
                                    />

                                    {/* Professional Situation Dropdown */}
                                    <div className="question-row">
                                        <div className="question-title d-flex align-items-center gap-2">
                                            <span>{formQuestions.situation.question}</span>
                                        </div>
                                        <div>
                                            <select
                                                className="form-select radius-8"
                                                value={filterCriteria.SituationProfessionnelle}
                                                onChange={(e) => handleFilterChange('SituationProfessionnelle', e.target.value)}
                                                disabled={loading || loadingSituations}
                                            >
                                                <option value="">Select Situation Professionnelle</option>
                                                {loadingSituations ? (
                                                    <option value="" disabled>Chargement...</option>
                                                ) : (
                                                    professionalSituations.map((situation) => (
                                                        <option key={situation.ID} value={situation.Value}>
                                                            {situation.Value}
                                                        </option>
                                                    ))
                                                )}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-12 mb-4">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                                Title <span className="text-danger-600">*</span>
                                <span className="text-gray-500 text-xs ms-2">(Max 1000 characters)</span>
                            </label>
                            <textarea
                                className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                                name="Title"
                                value={formData.Title}
                                onChange={handleInputChange}
                                placeholder="Enter notification title"
                                rows="2"
                                maxLength="1000"
                                disabled={loading}
                            />
                            <div className="text-xs text-gray-500 text-end mt-1">
                                {formData.Title.length}/1000
                            </div>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-md-12 mb-4">
                            <label className="form-label fw-semibold text-primary-light text-sm mb-2">
                                Description <span className="text-danger-600">*</span>
                                <span className="text-gray-500 text-xs ms-2">(Max 2500 characters)</span>
                            </label>
                            <textarea
                                className="form-control radius-8 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-600"
                                name="Description"
                                value={formData.Description}
                                onChange={handleInputChange}
                                placeholder="Enter notification description"
                                rows="6"
                                maxLength="2500"
                                disabled={loading}
                            />
                            <div className="text-xs text-gray-500 text-end mt-1">
                                {formData.Description.length}/2500
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center gap-3 mt-5">
                        <button
                            type="button"
                            className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Close
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? 'Sending...' : 'Send Notification'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SendNotificationModal;